import { useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ChevronDown, HelpCircle, MessageSquare, Sparkles } from "lucide-react";
import { useRef } from "react";
import GlowingOrb from "../effects/GlowingOrb";
import { useLanguage } from "@/context/LanguageContext";

const defaultContent = {
  section_label: "Preguntas Frecuentes",
  title: "¿Tienes Dudas?",
  subtitle: "Aquí encontrarás respuestas a las preguntas más comunes.",
  faqs: [
    { id: "1", question: "¿Cómo instalo el sistema operativo?", answer: "Nuestro equipo técnico se encarga de toda la instalación. No necesitas conocimientos técnicos. Realizamos la configuración completa, la migración de datos y la capacitación inicial." },
    { id: "2", question: "¿Qué requisitos técnicos necesita?", answer: "Solo necesitas un navegador web moderno y conexión a internet. Nuestro sistema está optimizado para funcionar en cualquier dispositivo con acceso a internet." },
    { id: "3", question: "¿Incluye soporte técnico?", answer: "Sí, todos nuestros planes incluyen soporte técnico. El plan Pro y Enterprise incluyen soporte prioritario 24/7 con tiempos de respuesta garantizados." },
    { id: "4", question: "¿Puedo migrar mis datos?", answer: "Sí, ofrecemos servicios de migración de datos completos. Nuestro equipo se encarga de trasladar toda tu información de forma segura y sin interrupciones." },
    { id: "5", question: "¿Qué tan seguro es?", answer: "Utilizamos encriptación de grado militar. Cumplimos con los más altos estándares de seguridad y todos los datos están protegidos bajo estrictos protocolos de confidencialidad." },
    { id: "6", question: "¿Puedo cambiar de plan?", answer: "Sí, puedes cambiar en cualquier momento. El upgrade es instantáneo y el downgrade se aplica al siguiente ciclo de facturación." }
  ]
};

const FAQSection = ({ content }) => {
  const data = { ...defaultContent, ...content };
  const [openId, setOpenId] = useState(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const toggleFaq = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section
      id="faq"
      ref={sectionRef}
      data-testid="faq-section"
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[#0b1221]" />
      
      {/* Background effects */}
      <GlowingOrb color="#3b82f6" size={400} className="bottom-0 right-0" />
      <div className="absolute inset-0 grid-pattern opacity-50" />

      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left side - Header */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="lg:sticky lg:top-32"
          >
            <motion.span
              className="text-[#3b82f6] text-sm font-semibold tracking-wider uppercase mb-4 inline-flex items-center gap-2"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 }}
            >
              <Sparkles className="w-4 h-4" />
              {t("faq_label")}
            </motion.span>

            <motion.h2
              data-testid="faq-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
            >
              {t("faq_title")}
            </motion.h2>

            <motion.p
              className="text-[#94a3b8] text-lg mb-4"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.4 }}
            >
              {t("faq_subtitle")}
            </motion.p>

            <motion.p
              className="text-[#3b82f6]/50 text-sm font-semibold tracking-widest uppercase mb-8"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.5 }}
            >
              Global Metafora APP
            </motion.p>

            {/* Help card */}
            <motion.div
              className="glass-card p-6 relative overflow-hidden group"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
              whileHover={{ borderColor: "rgba(59,130,246,0.5)", boxShadow: "0 0 30px rgba(59,130,246,0.15)" }}
            >
              <motion.div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.05), transparent)" }}
              />
              <div className="flex items-center gap-4 relative z-10">
                <motion.div
                  className="w-14 h-14 bg-[#3b82f6]/20 flex items-center justify-center flex-shrink-0"
                  whileHover={{ rotate: 10, scale: 1.1 }}
                >
                  <HelpCircle className="w-7 h-7 text-[#3b82f6]" />
                </motion.div>
                <div>
                  <p className="text-white font-semibold mb-1">{t("faq_need_help")}</p>
                  <motion.button
                    data-testid="faq-contact-btn"
                    onClick={() => document.querySelector("#contacto")?.scrollIntoView({ behavior: "smooth" })}
                    className="text-[#3b82f6] text-sm hover:underline inline-flex items-center gap-1"
                    whileHover={{ x: 5 }}
                  >
                    <MessageSquare className="w-4 h-4" />
                    {t("faq_contact_team")}
                    <motion.span
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      →
                    </motion.span>
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {/* Stats mini */}
            <motion.div
              className="mt-8 flex gap-6"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.7 }}
            >
              <div className="text-center">
                <p className="text-2xl font-bold text-[#3b82f6]">99%</p>
                <p className="text-[#94a3b8] text-xs">{t("faq_satisfaction")}</p>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl font-bold text-[#06b6d4]">24/7</p>
                <p className="text-[#94a3b8] text-xs">{t("faq_support")}</p>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl font-bold text-[#8b5cf6]">&lt;1h</p>
                <p className="text-[#94a3b8] text-xs">{t("faq_response")}</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right side - FAQ items */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="space-y-4"
          >
            {data.faqs.map((faq, index) => {
              const isOpen = openId === faq.id;
              return (
                <motion.div
                  key={faq.id}
                  data-testid={`faq-item-${faq.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.08 }}
                  className={`glass-card overflow-hidden transition-all duration-300 ${
                    isOpen ? "border-[#3b82f6]/40 shadow-[0_0_25px_rgba(59,130,246,0.1)]" : ""
                  }`}
                >
                  <button
                    data-testid={`faq-toggle-${faq.id}`}
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full flex items-center justify-between p-6 text-left group"
                  >
                    <div className="flex items-center gap-4 pr-4">
                      <motion.span
                        className="text-xs font-mono w-8 h-8 flex items-center justify-center flex-shrink-0"
                        animate={{
                          backgroundColor: isOpen ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.05)",
                          color: isOpen ? "#3b82f6" : "#94a3b8"
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </motion.span>
                      <span className={`font-medium transition-colors duration-300 ${isOpen ? "text-white" : "text-[#94a3b8] group-hover:text-white"}`}>
                        {faq.question}
                      </span>
                    </div>
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3, type: "spring" }}
                      className="flex-shrink-0"
                    >
                      <ChevronDown className={`w-5 h-5 transition-colors duration-300 ${isOpen ? "text-[#3b82f6]" : "text-[#94a3b8]"}`} />
                    </motion.div>
                  </button>
                  
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                      >
                        <div className="px-6 pb-6 pl-[4.5rem]">
                          <motion.div
                            className="w-12 h-px bg-gradient-to-r from-[#3b82f6] to-transparent mb-4"
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ delay: 0.1 }}
                          />
                          <p className="text-[#94a3b8] leading-relaxed">{faq.answer}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
